import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, first, tap} from 'rxjs/operators';
import { of } from 'rxjs';
import * as PickingsActions from '../actions/pickings.actions';
import { PickingsService } from '@app/services/pickings/picking.service';
import { Store } from '@ngrx/store';
import { CategoriesService } from '@app/services/categories/categories.service';
import { AreasService } from '@app/services/areas/areas.service';
import { selectPickingsDirectionState, selectPickingsSortState } from '../selectors/pickings.selectors';
import { ToastrService } from 'ngx-toastr';
import * as CoreActions from '@app/core/store/core.action';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

@Injectable()
export class PickingsEffects {

  loadPickings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PickingsActions.loadPickings),
      switchMap(action => {
        return this._pickings.getPickings('created_at', 'desc', action.query,0,100).pipe(
          map((data): any => {
            if(data.result!=null && data.result!='undefined' && data.result=='failed.access' && this.location.path()=='/pickings'){
              this.store$.dispatch(CoreActions.showPermissionMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.access')
              }));
            }

            return PickingsActions.loadPickingsSuccess({ pickings: data.pickings, total: data.total })
          }),
          catchError(error => {
            return of(PickingsActions.loadPickingsFailure({ error }))
          })
        )
      })
    );
  });

  searchPickings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PickingsActions.search),
      switchMap(action => {
        let direction: string;
        let sort: string;
        // this.store$.select(selectPickingsDirectionState).pipe(first()).subscribe(res => direction = res);
        // this.store$.select(selectPickingsSortState).pipe(first()).subscribe(res => sort = res);

        return this._pickings.searchPickings('product', 'ASC', action.query).pipe(
          map((data): any => {
            return PickingsActions.searchSuccess({ pickings: data.pickings, total: data.total })
          }),
          catchError(error => {
            return of(PickingsActions.searchFailure({ error }))
          })
        )
      })
    );
  });

  createPicking$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PickingsActions.savePicking),
      switchMap(action => {
        return this._pickings.onSave(action.form).pipe(
          map((response): any => {
            if(response.result!='undefined' && response.result=='failed.max'){
              this.store$.dispatch(CoreActions.showErrorMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.limitpicking'),
                show_upgrade: true
              }));
              return of(PickingsActions.savePickingFailure({ error: 'An error occurred.'}))
            }

            if(response.error){
              return PickingsActions.savePickingFailure({ error: 'An error occurred.' })
            }
            else{
              //action.controls['onHand'].setValue(parseInt(action.form.value.onHand)-parseInt(action.form.quantity));
              return PickingsActions.savePickingSuccess(response)
            }
            
          }),
          catchError(error => {
            return of(PickingsActions.savePickingFailure({ error}))
          })
        )
      })
    );
  });
  
  createOverride$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PickingsActions.saveOverride),
      switchMap(action => {
        return this._pickings.onOverride(action.form).pipe(
          map((response): any => {
            if(response.result!='undefined' && response.result=='failed.max'){
              this.store$.dispatch(CoreActions.showErrorMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.limitpicking'),
                show_upgrade: true
              }));
              return of(PickingsActions.saveOverrideFailure({ error: 'An error occurred.' }))
            }

            if(response.error && response.message!=''){
              this._toastr.error(this._translation.instant(response.message));
              return PickingsActions.saveOverrideFailure({ error: 'An error occurred.' })
            }
            else{
              return PickingsActions.saveOverrideSuccess(response)
            }
          }),
          catchError(error => {
            return of(PickingsActions.saveOverrideFailure({ error }))
          })
        )
      })
    );
  });

  updatePicking$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PickingsActions.updatePicking),
      switchMap(action => {
        return this._pickings.updatePicking(action.form).pipe(
          map((response): any => {
            if(response.error && response.message!=''){
              this._toastr.error(this._translation.instant(response.message));
              return PickingsActions.updatePickingFailure({ error: 'An error occurred.' })
            }

            return PickingsActions.updatePickingSuccess(response)
          }),
          catchError(error => {
            return of(PickingsActions.updatePickingFailure({ error }))
          })
        )
      })
    );
  });

  loadPicking$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PickingsActions.loadPicking),
      switchMap(action => {
        return this._pickings.getPicking(action.productMovementHistoryID).pipe(
          map(({ picking }): any => {
            return PickingsActions.loadPickingSuccess({ picking })
          }),
          catchError(error => {
            return of(PickingsActions.loadPickingFailure({ error }))
          })
        )
      })
    );
  });

  deletePickings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PickingsActions.deletePickings),
      switchMap(action => {
        return this._pickings.batchDelete(action.selected, action.reason).pipe(
          map(() => {
            return PickingsActions.deletePickingsSuccess(action.query);}),
          catchError(error => {
            return of(PickingsActions.deletePickingsFailure({ error }))
          })
          )
      })
    );
  });

  refreshPickingsAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PickingsActions.deletePickingsSuccess),
      switchMap(action => {
        return of(0).pipe(
          map(() => PickingsActions.loadPickings({query:''})),
          catchError(error => of(PickingsActions.deletePickingsFailure({ error }))
          ))
      })
    );
  });

  cleanPickingsAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PickingsActions.deletePickingsSuccess),
      switchMap(action => {
        return of(0).pipe(
          map(() => PickingsActions.setSelected({ selected: [] })),
          catchError(error => of(PickingsActions.deletePickingsFailure({ error }))
          ))
      })
    );
  });

  deletePicking$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PickingsActions.deletePicking),
      switchMap(action => {
        return this._pickings.onDelete(action.productMovementHistoryID, action.reason).pipe(
          tap(() => {
            // this._toastr.error(`Successfully deleted picking.`);
          }),
          map((): any => {
            return PickingsActions.deletePickingSuccess({ productMovementHistoryID: action.productMovementHistoryID })
          }),
          catchError(error => {
            return of(PickingsActions.deletePickingFailure({ error: error.message }))
          })
        )
      })
    );
  });


  setSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PickingsActions.setSort),
      switchMap(action => {
        return this._pickings.getPickings(action.sort, action.direction, action.query,0,100).pipe(
          map((data): any => {
            return PickingsActions.loadPickingsSuccess({ pickings: data.pickings, total: data.total })
          })
        )
      })
    );
  });

  constructor(
    private actions$: Actions, private _pickings: PickingsService,
    private store$: Store, private _categories: CategoriesService,
    private _areas: AreasService,
    private _toastr: ToastrService,
    public _router: Router,
    private location : Location,
    public _translation: TranslateService
  ) { }

}
